import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Tab from '@polestar/component-warehouse-react/components/tabs/Tab';
import useScrollEdges from '@polestar/component-warehouse-react/hooks/useScrollEdges';
import useScrollTo from '@polestar/component-warehouse-react/hooks/useScrollTo';
import useCallbackRef from '@polestar/component-warehouse-react/hooks/useCallbackRef';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useWidthCallback } from '@polestar/component-warehouse-react/hooks/useDimensions';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
export var TabsGap;
(function (TabsGap) {
    TabsGap["default"] = "default";
    TabsGap["condensed"] = "condensed";
})(TabsGap || (TabsGap = {}));
const Tabs = forwardRef(({ id, tabs, activeTab = 0, onTabClick, withBorder = false, maxLabelWidth = 140, minLabelWidth, monochrome, gap = TabsGap.default, onWheel, ...props }, ref) => {
    const memoizedId = useSSRSafeId('tabs', id);
    const [scrollRef, scrollElement] = useCallbackRef();
    const [focusedTab, setFocusedTab] = useState(0);
    const { handleScroll, reachedLeft, reachedRight } = useScrollEdges();
    const [containerRef, containerWidth] = useWidthCallback();
    const combinedRef = useCombinedRef(ref, containerRef);
    const { isRTL } = useDirection();
    const [tabsWidths, setTabsWidth] = useState(() => tabs.map(() => ({
        node: null,
        width: -1,
        offset: 0,
    })));
    const { motion, reset, stop } = useScrollTo(0, scrollElement, 'scrollLeft');
    const tabsLength = tabs.length;
    const refs = useMemo(() => {
        // stop any active scrolling
        stop();
        return tabs.map((_, index) => (node) => {
            if (node) {
                const handleWidthUpdate = () => {
                    setTabsWidth((oldTabsWidth) => {
                        const newTabs = [...oldTabsWidth];
                        newTabs[index] = {
                            node,
                            width: node.clientWidth,
                            offset: node.offsetLeft + node.clientWidth / 2,
                        };
                        return newTabs;
                    });
                };
                // node.clientWidth returns 0 when tabs are used inside something that is animating
                // For example in the accordion. If we get a 0px value will wait for the first draw
                if (node.clientWidth === 0) {
                    requestAnimationFrame(handleWidthUpdate);
                }
                else {
                    handleWidthUpdate();
                }
            }
        });
    }, [tabs, stop]);
    // Don't animate initial transition
    const allWidthsKnown = useMemo(() => tabsWidths.length === tabs.length && !tabsWidths.find((tab) => tab.width === -1), [tabsWidths, tabs]);
    const handleClick = useCallback((index) => {
        const currentScroll = (scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.scrollLeft) || 0;
        let newScroll;
        if (!isRTL) {
            // move into view when item is 80% from the left
            const fromRight = currentScroll + containerWidth * 0.8 < tabsWidths[index].offset;
            const fromLeft = currentScroll + containerWidth * 0.2 > tabsWidths[index].offset;
            newScroll = fromRight
                ? Math.round(tabsWidths[index].offset - containerWidth * 0.8 + tabsWidths[index].width * 0.5)
                : fromLeft
                    ? Math.round(tabsWidths[index].offset - containerWidth * 0.1 - tabsWidths[index].width * 0.5)
                    : currentScroll;
            if (newScroll < 0)
                newScroll = 0;
        }
        else {
            // move into view when item is 80% from the left
            const fromRight = tabsWidths[index].offset - currentScroll > -(containerWidth * 0.2);
            const fromLeft = tabsWidths[index].offset - currentScroll < -(containerWidth * 0.8);
            newScroll = fromRight
                ? Math.round(tabsWidths[index].offset + containerWidth * 0.1 + tabsWidths[index].width * 0.5)
                : fromLeft
                    ? Math.round(tabsWidths[index].offset + containerWidth * 0.8 + tabsWidths[index].width * 0.5)
                    : currentScroll;
            if (newScroll > 0)
                newScroll = 0;
        }
        // force scroll to center
        reset(); // resets the "from"
        motion.set(newScroll, false);
        onTabClick === null || onTabClick === void 0 ? void 0 : onTabClick(index);
    }, [scrollElement === null || scrollElement === void 0 ? void 0 : scrollElement.scrollLeft, containerWidth, tabsWidths, isRTL, reset, motion, onTabClick]);
    const handleWheel = useCallback((e) => {
        stop();
        onWheel === null || onWheel === void 0 ? void 0 : onWheel(e);
    }, [onWheel, stop]);
    const getNextEnabledIndex = useCallback((currentIndex, key) => {
        let nextIndex = currentIndex;
        const step = key === 'ArrowRight' ? 1 : -1;
        let count = 0;
        while (count < tabsLength) {
            nextIndex = (nextIndex + step + tabsLength) % tabsLength;
            if (!tabs[nextIndex].disabled) {
                return nextIndex;
            }
            count++;
        }
        return currentIndex;
    }, [tabs, tabsLength]);
    const handleKeyboardControls = useCallback((event) => {
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowLeft':
                const key = event.key;
                preventCancelableDefault(event);
                setFocusedTab((previousFocusTab) => {
                    var _a, _b, _c;
                    const newTab = getNextEnabledIndex(previousFocusTab, key);
                    if ((_a = tabsWidths[newTab]) === null || _a === void 0 ? void 0 : _a.node) {
                        (_c = (_b = tabsWidths[newTab]) === null || _b === void 0 ? void 0 : _b.node) === null || _c === void 0 ? void 0 : _c.focus();
                    }
                    return newTab;
                });
                break;
        }
    }, [getNextEnabledIndex, tabsWidths]);
    // ensure that when this component fits in the space the parent provides no gradients are shown
    const totalTabWidth = allWidthsKnown && tabs.length > 0
        ? tabsWidths[tabs.length - 1].width / 2 + tabsWidths[tabs.length - 1].offset
        : 0;
    const tabsOverflowContainer = Math.abs(totalTabWidth) > containerWidth;
    const tabIds = tabs.map((tab, index) => `${memoizedId}-${index}`);
    return (React.createElement(TabsContainer, { ref: combinedRef },
        withBorder && React.createElement(BottomBorder, { monochrome: monochrome }),
        React.createElement(Inner, { id: memoizedId, ...props, ref: scrollRef, onScroll: handleScroll, onWheel: handleWheel, onKeyDown: handleKeyboardControls, scrollOverflow: tabsOverflowContainer, startGradientVisible: ((isRTL && !reachedRight) || (!isRTL && !reachedLeft)) && tabsOverflowContainer, endGradientVisible: ((isRTL && !reachedLeft) || (!isRTL && !reachedRight)) && tabsOverflowContainer, isRTL: isRTL },
            React.createElement(List, { gap: gap, ...aria({
                    role: 'tablist',
                    owns: tabIds.join(' '),
                }) }, tabs.map((tab, index) => (React.createElement(Tab, { key: index, id: tabIds[index], parentId: memoizedId, ref: refs[index], ...tab, tabIndex: index === focusedTab ? 0 : -1, index: index, active: index === activeTab, onClick: handleClick, maxLabelWidth: maxLabelWidth, minLabelWidth: minLabelWidth, monochrome: monochrome, role: "tab" })))))));
});
Tabs.displayName = 'Tabs';
export default Tabs;
const TabsContainer = styled.div `
  position: relative;
`;
const hitboxOffset = createCustomSpacing(0.5);
const Inner = styled.div(({ scrollOverflow, startGradientVisible, endGradientVisible, isRTL }) => css(css `
      z-index: 2;
      position: relative;
      min-width: 0;

      /* to ensure Tab.tsx can breakout for the hitbox area
      * without breaking the scroll behaviour
      */
      margin: -${hitboxOffset} 0;
      padding: ${hitboxOffset} 0;
    `, scrollOverflow &&
    css `
        overflow-x: scroll;
        -webkit-overflow-scrolling: auto;
        -ms-overflow-style: none;
        scrollbar-width: none;

        mask-image: linear-gradient(90deg, transparent 0%, white 10%, white 90%, transparent 100%);
        mask-position: center;
        mask-size: 100%;

        &::-webkit-scrollbar {
          display: none;
          width: 0 !important;
          height: 0 !important;
        }
      `, scrollOverflow &&
    startGradientVisible &&
    !endGradientVisible &&
    css `
        mask-size: 110%;
        mask-position: ${isRTL ? '100%' : '0%'};
      `, scrollOverflow &&
    endGradientVisible &&
    !startGradientVisible &&
    css `
        mask-size: 110%;
        mask-position: ${isRTL ? '0%' : '100%'};
      `));
const List = styled('div', shouldNotForwardProps(true, 'gap'))(({ gap }) => css(css `
    list-style: none;
    position: relative;
    display: flex;
    margin: 0;
    white-space: nowrap;
    width: 0;
    z-index: 2;
    ${responsiveStyles({
    gap: {
        mobile: gap === TabsGap.condensed ? Spacings.medium : Spacings.large,
        tablet: gap === TabsGap.condensed ? Spacings.medium : Spacings.large,
        desktop: gap === TabsGap.condensed ? Spacings.large : Spacings.xLarge,
    },
})};
  `));
const BottomBorder = styled('div', shouldNotForwardProps(true, 'monochrome'))(({ theme, monochrome }) => css `
    position: absolute;
    height: ${createCustomSpacing(3 / 16)};
    /* Using this technique to create device px border height */
    background-image: linear-gradient(
      to bottom,
      transparent 50%,
      ${monochrome ? theme.tabs.monochrome.borderColor : theme.tabs.borderColor} 51%
    );
    background-size: 100% ${createCustomSpacing(3 / 16)};
    inset-inline-start: 0;
    inset-inline-end: 0;
    inset-block-end: ${createCustomSpacing(8 / 16)};
  `);
